<!-- src/lib/components/ChatPane.svelte -->
<!--
  Team chat pane — async-first stub.

  Design values communicated here:
    • No presence indicators (no green dots, no "last seen" timestamps)
    • Agent-aware channels (#agents)
    • Message-to-task as a native primitive
    • Closeable threads with owners

  The full implementation (realtime WebSocket, composer, message stream) will
  land in a follow-up once the backend wire contract is stable.
-->
<script lang="ts">
  // No reactive state needed for the static stub.
  // Script block kept so Svelte 5 runes mode is active for when we wire this up.
</script>

<div class="chat-root">
  <!-- Channel list sidebar -->
  <aside class="chat-channels">
    <div class="chat-section">
      <span class="chat-section-label">Channels</span>
      <ul class="channel-list">
        {#each ['#general', '#engineering', '#design', '#releases', '#agents'] as ch}
          <li>
            <button class="channel-btn" class:active={ch === '#engineering'}>
              <span class="channel-hash">#</span>
              <span class="channel-name">{ch.slice(1)}</span>
              {#if ch === '#agents'}
                <span class="agent-badge">🤖</span>
              {/if}
            </button>
          </li>
        {/each}
      </ul>
    </div>
    <div class="chat-section">
      <span class="chat-section-label">Direct</span>
      <ul class="channel-list">
        {#each ['team-standup', 'claude-code'] as dm}
          <li>
            <button class="channel-btn">
              <!-- No presence dot — async first -->
              <span class="dm-avatar">{dm[0].toUpperCase()}</span>
              <span class="channel-name">{dm}</span>
            </button>
          </li>
        {/each}
      </ul>
    </div>
  </aside>

  <!-- Main area -->
  <div class="chat-main">
    <div class="chat-coming-soon">
      <div class="soon-icon">💬</div>
      <h2 class="soon-title">Chat — coming soon</h2>
      <p class="soon-desc">Team channels, threads, and agent conversations — all in one place.</p>

      <div class="soon-principles">
        <div class="principle">
          <span class="p-icon">🔕</span>
          <div>
            <strong>Async by default</strong>
            <p>No green dots. No "last seen 2 minutes ago". Work at your own pace.</p>
          </div>
        </div>
        <div class="principle">
          <span class="p-icon">🤖</span>
          <div>
            <strong>Agent-aware channels</strong>
            <p>Agents post updates, ask for review, and surface blockers in context.</p>
          </div>
        </div>
        <div class="principle">
          <span class="p-icon">→</span>
          <div>
            <strong>Message to task</strong>
            <p>Hover any message. One click converts it to a tracked card — no copy-paste.</p>
          </div>
        </div>
        <div class="principle">
          <span class="p-icon">✓</span>
          <div>
            <strong>Closeable threads</strong>
            <p>Threads have owners. Mark them resolved. No open loops.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  /* ── Root layout ──────────────────────────────────────────────── */
  .chat-root {
    display: flex;
    width: 100%;
    height: 100%;
    overflow: hidden;
    color: oklch(0.85 0.04 310);
    font-size: 0.85rem;
  }

  /* ── Channel sidebar ──────────────────────────────────────────── */
  .chat-channels {
    width: 200px;
    flex-shrink: 0;
    background: oklch(0.095 0.018 275);
    border-right: 1px solid oklch(0.30 0.05 275 / 0.15);
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    padding: 1rem 0;
    overflow-y: auto;
  }

  .chat-section {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .chat-section-label {
    font-size: 0.60rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: oklch(0.50 0.04 310 / 0.5);
    padding: 0 0.75rem 0.25rem;
  }

  .channel-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .channel-btn {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    width: 100%;
    background: transparent;
    border: none;
    color: oklch(0.68 0.04 310 / 0.7);
    padding: 0.35rem 0.75rem;
    font-size: 0.80rem;
    text-align: left;
    cursor: pointer;
    transition: background-color 100ms ease, color 100ms ease;
    border-radius: 0;
  }

  .channel-btn:hover {
    background: oklch(0.72 0.28 310 / 0.06);
    color: oklch(0.92 0.06 310);
  }

  .channel-btn.active {
    background: oklch(0.72 0.28 310 / 0.16);
    color: oklch(0.96 0.12 310);
  }

  .channel-hash {
    color: oklch(0.55 0.05 310 / 0.5);
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.80rem;
    flex-shrink: 0;
  }

  .channel-btn.active .channel-hash {
    color: oklch(0.78 0.26 310 / 0.7);
  }

  .channel-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }

  .agent-badge {
    font-size: 0.65rem;
    background: oklch(0.72 0.28 310 / 0.20);
    border-radius: 999px;
    padding: 0.05rem 0.35rem;
    line-height: 1.4;
    flex-shrink: 0;
  }

  .dm-avatar {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    background: oklch(0.30 0.06 275 / 0.55);
    color: oklch(0.80 0.06 310);
    font-size: 0.68rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    /* No presence ring — async first */
  }

  /* ── Main content area ────────────────────────────────────────── */
  .chat-main {
    flex: 1;
    background: oklch(0.075 0.014 275);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow-y: auto;
    padding: 2rem;
  }

  /* ── Coming soon panel ────────────────────────────────────────── */
  .chat-coming-soon {
    max-width: 520px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.65rem;
  }

  .soon-icon {
    font-size: 2.5rem;
    line-height: 1;
    margin-bottom: 0.25rem;
  }

  .soon-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: oklch(0.94 0.08 310);
    letter-spacing: -0.01em;
  }

  .soon-desc {
    margin: 0;
    font-size: 0.875rem;
    color: oklch(0.60 0.05 310 / 0.75);
    line-height: 1.6;
    max-width: 380px;
  }

  /* ── Principles grid ──────────────────────────────────────────── */
  .soon-principles {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    width: 100%;
    margin-top: 1rem;
  }

  .principle {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    background: oklch(0.12 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.20);
    border-radius: 10px;
    padding: 0.85rem 1rem;
    text-align: left;
  }

  .p-icon {
    font-size: 1.1rem;
    line-height: 1;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }

  .principle strong {
    display: block;
    font-size: 0.82rem;
    font-weight: 600;
    color: oklch(0.92 0.08 310);
    margin-bottom: 0.25rem;
  }

  .principle p {
    margin: 0;
    font-size: 0.76rem;
    color: oklch(0.58 0.04 310 / 0.80);
    line-height: 1.55;
  }
</style>
